import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, Lock, ArrowLeft } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";

export default function AdminLogin() {
  const { login, user, ready } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (ready && user) navigate("/admin");
  }, [ready, user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      navigate("/admin");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  const inputClass =
    "w-full bg-transparent border-b border-white/20 focus:border-[#d4c4b7] text-stone-100 placeholder:text-stone-600 py-3 text-lg font-light outline-none transition-colors";

  return (
    <div className="min-h-screen bg-[#0a0a0c] flex items-center justify-center px-6">
      <div className="grain-overlay" aria-hidden />
      <div className="w-full max-w-md">
        <a href="/" className="inline-flex items-center gap-2 overline text-stone-500 hover:text-stone-300 transition-colors mb-12">
          <ArrowLeft className="h-3.5 w-3.5" /> Back to site
        </a>

        <img src="/brand/logo.png" alt="Perth Tiling WA" className="h-16 w-auto object-contain mb-10" />

        <p className="overline text-[#d4c4b7] mb-3">Admin Access</p>
        <h1 className="font-serif font-light text-stone-50 text-4xl mb-10">Enquiries Portal</h1>

        <form onSubmit={submit} data-testid="login-form" className="space-y-8">
          <div>
            <label className="overline text-stone-500 block mb-3">Email</label>
            <input data-testid="login-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="admin@perthtilingwa.com.au" className={inputClass} />
          </div>
          <div>
            <label className="overline text-stone-500 block mb-3">Password</label>
            <input data-testid="login-password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" className={inputClass} />
          </div>

          {error && <p data-testid="login-error" className="text-red-400 text-sm">{error}</p>}

          <button
            data-testid="login-submit"
            type="submit"
            disabled={loading}
            className="group w-full inline-flex items-center justify-center gap-3 bg-[#d4c4b7] text-[#0a0a0c] hover:bg-white disabled:opacity-60 px-8 py-4 text-xs uppercase tracking-[0.22em] transition-colors"
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Lock className="h-4 w-4" />}
            {loading ? "Signing in" : "Sign In"}
          </button>
        </form>
      </div>
    </div>
  );
}
